/**
 * Assign Rights Page
 *
 * Dedicated page for assigning rights to a role using transfer UI
 * Features: left-right transfer, bulk operations, search, save changes
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { IconArrowLeft, IconShield, IconAlertCircle } from '@tabler/icons-react';
import {
  Container,
  Stack,
  Group,
  Button,
  Text,
  Loader,
  Alert,
  Card,
  Title,
} from '../../../components/ui';
import { FadeIn } from '../../../components/animations';
import { toast } from '../../../components/ui/providers/AlertProvider';
import { RightsTransfer } from './components/RightsTransfer';
import { usePermissionManagement } from './hooks/usePermissionManagement';
import type { RoleModelView, RightModelView } from './types';

export default function AssignRightsPage() {
  const { id: roleId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const permissionMgmt = usePermissionManagement();

  // State
  const [role, setRole] = useState<RoleModelView | null>(null);
  const [allRights, setAllRights] = useState<RightModelView[]>([]);
  const [availableRights, setAvailableRights] = useState<RightModelView[]>([]);
  const [assignedRights, setAssignedRights] = useState<RightModelView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch role and rights data
   */
  const fetchData = useCallback(async () => {
    if (!roleId) {
      setError('Role ID is required');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch role details, all rights, and assigned rights in parallel
      const [roleData, allRightsData, assignedRightsData] = await Promise.all([
        permissionMgmt.getRoleById(roleId),
        permissionMgmt.fetchRights(),
        permissionMgmt.fetchRoleRights(roleId),
      ]);

      setRole(roleData);
      setAllRights(allRightsData);
      setAssignedRights(assignedRightsData);

      // Split into available and assigned
      const assignedIds = new Set(assignedRightsData.map((r) => r.id));
      const available = allRightsData.filter((r) => !assignedIds.has(r.id));

      setAvailableRights(available.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (err: any) {
      const errorMessage = err?.message || 'Failed to load role and rights data';
      setError(errorMessage);
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roleId]);

  /**
   * Load data on mount
   */
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /**
   * Handle assigned rights change - auto-save on each change
   */
  const handleAssignedChange = useCallback(
    async (newAssigned: RightModelView[], addedIds: number[], removedIds: number[]) => {
      if (!roleId) return;

      // Update local state immediately for UI feedback
      setAssignedRights(newAssigned);

      // Update available rights to prevent duplicates
      // Remove added rights from available, add removed rights back to available
      setAvailableRights((prev) => {
        let updated = [...prev];

        // Remove newly assigned rights from available
        if (addedIds.length > 0) {
          updated = updated.filter((r) => !addedIds.includes(r.id));
        }

        // Add removed rights back to available
        if (removedIds.length > 0) {
          const rightsToAddBack = allRights.filter((r) => removedIds.includes(r.id));
          updated = [...updated, ...rightsToAddBack];
        }

        return updated.sort((a, b) => a.name.localeCompare(b.name));
      });

      // Execute changes in parallel
      try {
        await Promise.all([
          ...addedIds.map((id) => permissionMgmt.assignRightToRole(roleId, id)),
          ...removedIds.map((id) => permissionMgmt.revokeRightFromRole(roleId, id)),
        ]);
      } catch (err: any) {
        console.error('Error updating rights:', err);
        toast.error('Failed to update rights', err.message);
        // Revert on error
        await fetchData();
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roleId, allRights]
  );

  /**
   * Handle back navigation
   */
  const handleBack = () => {
    navigate('/admin/permissions');
  };

  // Loading state
  if (loading) {
    return (
      <Container size="xl">
        <Group justify="center" p="xl">
          <Loader size="lg" />
        </Group>
      </Container>
    );
  }

  // Error state
  if (error || !role) {
    return (
      <Container size="xl">
        <FadeIn>
          <Stack gap="lg">
            <Alert color="red" icon={<IconAlertCircle size={16} />} title="Error">
              {error || 'Failed to load role'}
            </Alert>
            <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={handleBack}>
              Back to Permissions
            </Button>
          </Stack>
        </FadeIn>
      </Container>
    );
  }

  return (
    <Container size="xl">
      <FadeIn>
        <Stack gap="lg">
          {/* Header */}
          <Group justify="space-between" align="flex-start">
            <Stack gap="xs">
              <Button
                variant="subtle"
                size="xs"
                leftSection={<IconArrowLeft size={14} />}
                onClick={handleBack}
              >
                Back to Permissions
              </Button>
              <Group gap="sm">
                <IconShield size={28} />
                <div>
                  <Title order={2}>Assign Rights to Role</Title>
                  <Text c="dimmed" size="sm">
                    Managing rights for: <strong>{role.name}</strong>
                  </Text>
                  {role.description && (
                    <Text c="dimmed" size="xs" mt={4}>
                      {role.description}
                    </Text>
                  )}
                </div>
              </Group>
            </Stack>
          </Group>

          {/* Transfer Component */}
          <Card>
            <RightsTransfer
              roleId={roleId!}
              roleName={role.name}
              initialAvailable={availableRights}
              initialAssigned={assignedRights}
              onChange={handleAssignedChange}
            />
          </Card>

          {/* Bottom Back Button */}
          <Group justify="flex-end">
            <Button variant="subtle" onClick={handleBack}>
              Back to Permissions
            </Button>
          </Group>
        </Stack>
      </FadeIn>
    </Container>
  );
}
